<template>
	<div class="review-popup-content container mb-5 mt-md-5 cursor-default">
		<div
			class="d-flex flex-column flex-md-row bg-primary-white shadow-3 border-grey-300 p-4 mt-3 m-md-3 position-relative"
			style="border-radius: var(--border-radius-card-secondary);"
		>
			<button
				@click="handleClosePopup"
				class="ts-x-black ts-x-md position-absolute review-popup-cls"
				style="
					position:absolute;
					top:2px;
					right:16px;
					left:auto;
				"
			></button>
			<div class="pe-5 d-flex flex-column justify-content-start gap-1 user-info-box">
				<div class="author-name-box d-flex body-strong text-grey-900">
					<span v-if="formattedSurname">{{ formattedSurname }}</span>
					<span>{{ " " }}</span>
					<span v-if="review.authorName">{{ review.authorName }}</span>
					
				</div>

				<div class="body text-grey-600 text-nowrap">
					<span class="content-date">{{ getCreatedDatePretty(review.creationDate) }}</span>
				</div>

				
				<div class="mb-2 d-flex align-items-center">
					<span class="content-rating body-strong text-grey-900 me-1 mt-1">
						{{ formattedRatingByLocale }}/5
					</span>
					<svg class="icon mb-1 text-primary-blue" width="16" height="16" style="position: relative; top: 2px;">
						<use href="#___icon___star-full"></use>
					</svg>
				</div>

			</div>
			<div class="flex-grow-1 body review-body-column">
				<template v-if="review.customerComment">
					
					<TranslatedComment :review="review" />


					<div v-if="review.liheAnswer"
						class="bg-blue-100 p-4 mt-4"
						style="border-radius: var(--border-radius-default);"
					>
						<strong class="d-block mb-2 caption-strong text-grey-800">
							{{ content.reviews_response || 'Réponse de travelski' }}
						</strong>
					<!-- Answer text and toggle inline -->
						<div class="d-flex align-items-center justify-content-between">
							<p :style="clampStyle"
							class="m-0 caption text-grey-800 flex-grow-1"
							style="line-height:1.5;"
							>
							{{ displayedAnswer }}
							</p>
							 <strong><span
								v-if="shouldShowToggle"
								@click="isAnswerExpanded = !isAnswerExpanded"
								class="d-inline-block mt-2 link-small cursor-pointer"
							>
								{{ isAnswerExpanded
								? (content.reviews_seeLess || 'Lire moins')
								: (content.reviews_seeMore_popup || 'Lire la suite')
								}}
          					</span></strong>
						</div>

					</div>



				</template>


			</div>
		</div>
	</div>
</template>

<script setup>

import { useStore } from 'vuex'
import { computed, watchEffect } from 'vue'
import { ref } from 'vue'
import TranslatedComment from '@/vue/reviews-translation/components/popups/translatedComment.vue'
import { getOverallReviewRatingValue } from '@/vue/reviews-translation/utils/overallReviewRating.js'

const isAnswerExpanded = ref(false)


const props = defineProps({
	review: {
		type: Object,
		default: () => ({}),
	},
	open: {
		type: Boolean,
		default: false,
	},
	ratings: {
		type: Array,
		default: () => [],
	},
})

const emit = defineEmits(['closePopup'])

const store = useStore()
const language = computed(() => store.state.language)
const content = computed(() => store.state.content)

const formattedSurname = computed(() => {
  if (!props.review.authorSurname) return ''
  return props.review.authorSurname.charAt(0).toUpperCase() + '.'
})

const companyLangToLocale = new Map([
	['fr', 'fr-FR'],
	['en', 'en-US'],
	['nl', 'nl-NL'],
])

watchEffect(() => {
	if (props.review.liheAnswer) {
		console.log('[ReviewPopup] Hotel answer received:', props.review.liheAnswer)
	}
})

function getCreatedDatePretty(dateStr) {
	const dateObj = new Date(dateStr)
	const options = { year: 'numeric', month: 'long', day: 'numeric' }

	let locale = language.value

	if (locale === 'fr-FR-CSE') {
		const match = document.cookie.match('(^|;)\\s*companyLang\\s*=\\s*([^;]+)')
		if (match && match[2]) {
			const shortLang = decodeURIComponent(match[2]).trim()
			const mappedLocale = companyLangToLocale.get(shortLang)
			locale = mappedLocale || 'en-US'
		} else {
			locale = 'en-US'
		}
	}

	try {
		return dateObj.toLocaleString(locale, options)
	} catch (error) {
		console.error('Date formatting error:', error)
		return dateObj.toLocaleString('en-US', options)
	}
}

function handleClosePopup() {
	emit('closePopup')
}


const formattedRatingByLocale = computed(() => {
	const overall = getOverallReviewRatingValue(props.review) ?? 0
	const raw = overall.toFixed(1).replace(/\.0$/, '') || '0'
	if (language.value === 'fr-FR' || language.value === 'fr-FR-CSE') {
		return raw.replace('.', ',')
	}
	return raw
})

function extractSiteSegment(raw) {
  if (!raw || typeof raw !== 'string') return ''
  const codeMatch = language.value.slice(0,2).toLowerCase()
  const parts = raw.split(/\s*\|\s*/)
  const map = {}
  parts.forEach(part => {
    const m = part.match(/^(\w{2}):\s*(.+)$/s)
    if (m) map[m[1].toLowerCase()] = m[2].trim()
  })
  return map[codeMatch] || raw
}

const displayedAnswer = computed(() => extractSiteSegment(props.review.liheAnswer))


// Style for clamp
const clampStyle = computed(() => isAnswerExpanded.value
  ? {}  // no clamp when expanded
  : {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: '1',
      overflow: 'hidden'
    }
)
// Show toggle only if text overflows one line (rough heuristic)
const shouldShowToggle = computed(() => {
  return displayedAnswer.value.split('\n').length > 1 || displayedAnswer.value.length > 100
})

</script>

<style>
button.ts-x-black.ts-x-md.review-popup-cls {
  position: absolute !important;
  top: 16px !important;
  right: 16px !important;
  left: auto !important;
  z-index: 10;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.customer-comment-box {
	padding-right: 20px;
	border-radius: var(--border-radius-default);
}

.user-info-box {
	padding: 12px;
}

.author-name-box{
	font-size:20px;
}

.review-body-column {
  flex: 1 1 0%;
  max-width: 100%;
  min-width: 0;
}

.hotel-comment-box {
  background-color: var(--blue-100);
  padding: 12px 16px;
  border-radius: var(--border-radius-default);
  line-height: 1.4;
  width: 100%;
  box-sizing: border-box;
  word-break: break-word;
  overflow-wrap: break-word;
}

.answer-line {
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  overflow: hidden;
}

.answer-text {
  display: inline-block;
  white-space: normal;
  word-break: break-word;
}

.clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  max-width: calc(100% - 90px); 
}

@media (max-width: 768px) {
  .clamped {
    -webkit-line-clamp: 3;
    line-clamp: 3;
  }
}

.original-language-line {
	font-size: 0.875rem; /* .caption size */
	color: var(--grey-800);
}


</style>